import { Button, Icons, Input } from '@healthinal/ui';
import { useRef, useState } from 'react';
import { AddressContactDto } from '../../../../../api/generated/model/addressContactDto';
import { CreateAddressContactDialog } from '../../../../components/patient-address/CreateAddressContactDialog';
import { MasterdataTable } from '../MasterTable';
import { ContactPersonHistoryModal } from './ContactPersonHistoryModal';
import { ContactPersonTableRow } from './ContactPersonTableRow';
import { useContactPersonTable } from './useContactPersonTable';

type ContactDialogState = {
  open: boolean;
  contact: AddressContactDto | null;
};

type HistoryModalState = {
  open: boolean;
  contact: AddressContactDto | null;
};

function ContactPersonTableHeader() {
  return (
    <>
      <th style={{ width: 80 }}>Anrede</th>
      <th style={{ width: 140 }}>Name</th>
      <th style={{ width: 140 }}>Vorname</th>
      <th style={{ width: 160 }}>Funktion</th>
      <th style={{ width: 150 }}>Telefon Nr.</th>
      <th style={{ width: 220 }}>E-Mail</th>
      <th style={{ width: 180 }}>Institution</th>
      <th style={{ width: 65 }}>Aktiv</th>
      <th style={{ width: 100 }}>Verlauf</th>
    </>
  );
}

export function ContactPersonTable() {
  const {
    contacts,
    isLoading,
    page,
    totalPages,
    search,
    setSearch,
    setPage,
    handleToggleActive,
    handleCreate,
    handleUpdate,
    isTogglePending,
    isCreatePending,
    isUpdatePending,
  } = useContactPersonTable();
  const [dialogState, setDialogState] = useState<ContactDialogState>({ open: false, contact: null });
  const [historyModalState, setHistoryModalState] = useState<HistoryModalState>({ open: false, contact: null });
  const editingContactRef = useRef<AddressContactDto | null>(null);

  const openDialog = (contact: AddressContactDto | null) => {
    editingContactRef.current = contact;
    setDialogState({ open: true, contact });
  };

  return (
    <>
      <MasterdataTable
        title="Ansprechpersonen"
        headerActions={
          <>
            <Input
              placeholder="Suche..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                width: 260,
                maxWidth: '100%',
                flexShrink: 0,
              }}
            />
            <Button color="primary" endDecorator={<Icons.Add />} onClick={() => openDialog(null)}>
              Ansprechperson erfassen
            </Button>
          </>
        }
        isLoading={isLoading}
        isEmpty={contacts.length === 0}
        emptyText="Keine Daten vorhanden."
        colSpan={9}
        page={page}
        totalPages={totalPages}
        onPrevPage={() => setPage((prev) => Math.max(0, prev - 1))}
        onNextPage={() => setPage((prev) => Math.min(Math.max(0, totalPages - 1), prev + 1))}
        disablePrev={page === 0 || isLoading}
        disableNext={page + 1 >= totalPages || isLoading}
        header={<ContactPersonTableHeader />}>
        {contacts.map((contact, index) => (
          <tr
            key={contact.id ?? `${contact.firstName}-${contact.lastName}-${index}`}
            onClick={() => openDialog(contact)}
            style={{ textAlign: 'left', cursor: 'pointer' }}>
            <ContactPersonTableRow
              contact={contact}
              onToggleActive={handleToggleActive}
              onShowHistory={(selectedContact) => setHistoryModalState({ open: true, contact: selectedContact })}
              isPending={isTogglePending}
            />
          </tr>
        ))}
      </MasterdataTable>

      <CreateAddressContactDialog
        open={dialogState.open}
        contact={dialogState.contact}
        isLoading={isCreatePending || isUpdatePending}
        onClose={(result) => {
          const editingContact = editingContactRef.current;
          setDialogState({ open: false, contact: null });
          editingContactRef.current = null;

          if (!result) return;

          if (editingContact?.id) {
            handleUpdate(result);
          } else {
            handleCreate(result);
          }
        }}
      />

      <ContactPersonHistoryModal
        open={historyModalState.open}
        onClose={() => setHistoryModalState({ open: false, contact: null })}
        contact={historyModalState.contact}
      />
    </>
  );
}
